import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Clock, Play, ToggleLeft, ToggleRight, RefreshCw, AlertTriangle, CheckCircle } from 'lucide-react';
import Swal from 'sweetalert2'; // <-- Import SweetAlert2
import 'sweetalert2/dist/sweetalert2.min.css'; // <-- Import the styles
import './JobScheduler.css';

const API_URL = process.env.REACT_APP_API_URL;
const JOBS_API_URL = `${API_URL}/api/jobs`;

// Helper to extract HH:MM time from a cron string like "M H * * *"
const extractTimeFromCron = (cronString) => {
    if (!cronString) return '';
    const parts = cronString.split(' ');
    if (parts.length < 2) return '';
    const minute = parts[0];
    const hour = parts[1];
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

const JobScheduler = () => {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchJobs = useCallback(async () => {
        try {
            const response = await fetch(JOBS_API_URL);
            if (!response.ok) throw new Error('Failed to fetch jobs from server.');
            const data = await response.json();
            setJobs(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchJobs();
         window.addEventListener('focus', fetchJobs);
         return () => {
            window.removeEventListener('focus', fetchJobs);
        };
    }, [fetchJobs]);

    const handleUpdateJob = async (id, updateData) => {
        try {
            await fetch(`${JOBS_API_URL}/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updateData),
            });
            // ADDED: Success Toast for better UX
            const Toast = Swal.mixin({
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
            });
            Toast.fire({ icon: 'success', title: 'Job updated successfully!' });
            await fetchJobs();
        } catch (err) {
            // MODIFIED: Replaced alert with Swal
            Swal.fire({
                icon: 'error',
                title: 'Update Failed',
                text: err.message || 'Could not update the job. Please try again.',
            });
            console.error("Update failed:", err);
        }
    };

    const handleRunNow = (id) => {
        // MODIFIED: Replaced window.confirm with Swal
        Swal.fire({
            title: 'Run this job now?',
            text: "This will trigger the job to run immediately in the background.",
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, run it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await fetch(`${JOBS_API_URL}/${id}/run`, { method: 'POST' });
                    // Replaced alert with Swal for success
                    Swal.fire({
                        icon: 'success',
                        title: 'Job Triggered!',
                        text: 'The job is now running. The status will update shortly.',
                        timer: 4000,
                        showConfirmButton: false,
                    });
                    setTimeout(fetchJobs, 5000); 
                } catch (err) {
                    // Replaced alert with Swal for error
                    Swal.fire({
                        icon: 'error',
                        title: 'Failed to Trigger',
                        text: err.message || 'The job could not be started.',
                    });
                }
            }
        });
    };

    if (loading) {
        return (
            <>
                <Helmet><title>Loading Job Scheduler...</title></Helmet>
                <div className="scheduler-loading">Loading Job Scheduler...</div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Helmet><title>Error - Job Scheduler</title></Helmet>
                <div className="scheduler-error">Error: {error}</div>
            </>
        );
    }

    const syncJobs = jobs.filter(j => j.type === 'sync');
    const emailJobs = jobs.filter(j => j.type === 'email');

    // ... The rest of the component JSX remains unchanged ...
    const renderJobList = (jobList, title) => (
    <div className="job-category">
        <h2>{title}</h2>
        <div className="job-list">
            {jobList.map(job => (
                <div key={job._id} className={`job-card status-${job.status}`}>
                    <div className="job-header">
                        <h3 className="job-title">{job.displayName}</h3>
                        <span className={`job-status-badge status-${job.status}`}>
                            {job.status === 'success' || job.status === 'idle' ? <CheckCircle size={16} /> :
                             job.status === 'failed' ? <AlertTriangle size={16} /> :
                             <RefreshCw size={16} className="spinner" />}
                            {job.status}
                        </span>
                    </div>
                    <p className="job-description">{job.description}</p>
                    
                    <div className="job-details">
                        <div className="job-detail-item">
                            <strong>Daily Schedule (Time):</strong> 
                            <input
                                type="time"
                                className="schedule-input time-input"
                                defaultValue={extractTimeFromCron(job.schedule)}
                                onBlur={(e) => handleUpdateJob(job._id, { dailyRunTime: e.target.value })}
                                title="Set the time for the job to run every day."
                            />
                        </div>
                        
                        {job.type === 'email' && (
                            <div className="job-detail-item">
                                <strong>Delay (Days):</strong>
                                <input
                                    type="number"
                                    min="0"
                                    className="delay-input"
                                    defaultValue={job.delayDays || 0}
                                    onBlur={(e) => handleUpdateJob(job._id, { delayDays: Number(e.target.value) })}
                                    title="Wait this many days after a user meets the condition before sending the email."
                                />
                            </div>
                        )}

                        <div className="job-detail-item"><strong>Next Run:</strong> {job.nextRun ? new Date(job.nextRun).toLocaleString() : 'N/A'}</div>
                        <div className="job-detail-item"><strong>Last Run:</strong> {job.lastRun ? new Date(job.lastRun).toLocaleString() : 'Never'}</div>
                    </div>

                    <div className="job-actions">
                        <button onClick={() => handleUpdateJob(job._id, { isActive: !job.isActive })} className={`action-btn toggle-btn ${job.isActive ? 'active' : ''}`}>
                            {job.isActive ? <ToggleRight /> : <ToggleLeft />}
                            {job.isActive ? 'Enabled' : 'Disabled'}
                        </button>
                        <button onClick={() => handleRunNow(job._id)} className="action-btn run-btn" disabled={job.status === 'running'}>
                            <Play size={16} /> Run Now
                        </button>
                    </div>
                    
                    {job.log && <div className="job-log"><strong>Last Log:</strong> {job.log}</div>}
                </div>
            ))}
        </div>
    </div>
    );

    return (
        <div className="job-scheduler-container">
            <Helmet>
                <title>Job Scheduler | GN VISA Admin</title>
                <meta name="description" content="Manage and monitor automated background tasks like data synchronization and email jobs." />
            </Helmet>
            {renderJobList(syncJobs, "Data Synchronization Jobs")}
            {renderJobList(emailJobs, "Automated Email Jobs")}
        </div>
    );
};

export default JobScheduler;